import React, { useEffect } from 'react';
import { Container, Row, Col, Button, Form, InputGroup } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { 
  FaFacebookF, 
  FaTwitter, 
  FaInstagram, 
  FaYoutube, 
  FaPhone, 
  FaEnvelope, 
  FaMapMarkerAlt,
  FaArrowRight,
  FaBookOpen,
  FaGraduationCap,
  FaShieldAlt,
  FaPaperPlane
} from 'react-icons/fa';
import 'animate.css';

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    // Add animation classes when elements come into view
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const element = entry.target as HTMLElement;
          const animation = element.getAttribute('data-animation');
          if (animation) {
            element.classList.add('animate__animated', animation);
            observer.unobserve(entry.target);
          }
        }
      });
    }, { threshold: 0.1 });

    document.querySelectorAll('.animate-on-scroll').forEach(item => {
      observer.observe(item);
    });

    return () => {
      document.querySelectorAll('.animate-on-scroll').forEach(item => {
        observer.unobserve(item);
      });
    };
  }, []);

  return (
    <footer className="bg-dark text-light py-5 mt-auto footer-gradient">
      <div className="footer-shapes">
        <div className="footer-shape footer-shape-1"></div>
        <div className="footer-shape footer-shape-2"></div>
        <div className="footer-shape footer-shape-3"></div>
      </div>
      
      <Container className="position-relative">
        <Row className="mb-5">
          <Col lg={4} md={6} className="mb-4 mb-lg-0">
            <div className="mb-4 animate-on-scroll" data-animation="animate__fadeInUp">
              <h4 className="text-white mb-4 fw-bold">
                <FaBookOpen className="me-2 text-primary footer-icon-pulse" />
                منصة السامي التعليمية
              </h4>
              <p className="text-muted">
                منصة تعليمية متكاملة تهدف إلى توفير تجربة تعليمية متميزة لطلاب المرحلة الثانوية من خلال محتوى تعليمي عالي الجودة ونظام تفاعلي متطور.
              </p>
            </div>
            <div className="animate-on-scroll" data-animation="animate__fadeInUp">
              <h5 className="text-white mb-3">تابعنا على</h5>
              <div className="d-flex gap-2">
                <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="social-icon-link facebook">
                  <FaFacebookF />
                </a>
                <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="social-icon-link twitter">
                  <FaTwitter />
                </a>
                <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="social-icon-link instagram">
                  <FaInstagram />
                </a>
                <a href="https://youtube.com" target="_blank" rel="noopener noreferrer" className="social-icon-link youtube">
                  <FaYoutube />
                </a>
              </div>
            </div>
          </Col>
          
          <Col lg={2} md={6} className="mb-4 mb-lg-0">
            <div className="animate-on-scroll" data-animation="animate__fadeInUp">
              <h5 className="text-white mb-4">
                <FaGraduationCap className="me-2 text-primary footer-icon-pulse" />
                روابط سريعة
              </h5>
              <ul className="list-unstyled footer-links">
                <li className="mb-2">
                  <Link to="/" className="footer-link">
                    <FaArrowRight className="me-2 text-primary footer-link-icon" size={12} />
                    <span>الرئيسية</span>
                  </Link>
                </li>
                <li className="mb-2">
                  <Link to="/courses" className="footer-link">
                    <FaArrowRight className="me-2 text-primary footer-link-icon" size={12} />
                    <span>الكورسات</span>
                  </Link>
                </li>
                <li className="mb-2">
                  <Link to="/teachers" className="footer-link">
                    <FaArrowRight className="me-2 text-primary footer-link-icon" size={12} />
                    <span>المعلمون</span>
                  </Link>
                </li>
                <li className="mb-2">
                  <Link to="/about-us" className="footer-link">
                    <FaArrowRight className="me-2 text-primary footer-link-icon" size={12} />
                    <span>من نحن</span>
                  </Link>
                </li>
                <li className="mb-2">
                  <Link to="/get-in-touch" className="footer-link">
                    <FaArrowRight className="me-2 text-primary footer-link-icon" size={12} />
                    <span>اتصل بنا</span>
                  </Link>
                </li>
              </ul>
            </div>
          </Col>
          
          <Col lg={6} md={6}>
            <div className="animate-on-scroll" data-animation="animate__fadeInUp">
              <h5 className="text-white mb-4">
                <FaEnvelope className="me-2 text-primary footer-icon-pulse" />
                اشترك في النشرة الإخبارية
              </h5>
              <p className="text-muted mb-4">اشترك ليصلك كل جديد عن الدورات والعروض الحصرية</p>
              <Form>
                <InputGroup className="mb-3 newsletter-form">
                  <Form.Control
                    placeholder="البريد الإلكتروني"
                    aria-label="البريد الإلكتروني"
                    className="py-2 newsletter-input"
                  />
                  <Button variant="primary" id="button-addon2" className="newsletter-button">
                    <FaPaperPlane className="newsletter-icon" /> اشتراك
                  </Button>
                </InputGroup>
              </Form>
            </div>
            
            <div className="mt-4 animate-on-scroll" data-animation="animate__fadeInUp">
              <h5 className="text-white mb-3">معلومات الاتصال</h5>
              <ul className="list-unstyled">
                <li className="d-flex mb-3 footer-contact-item">
                  <FaMapMarkerAlt className="text-primary mt-1 me-3 footer-contact-icon" />
                  <span className="text-muted">القاهرة، مصر</span>
                </li>
                <li className="d-flex mb-3 footer-contact-item">
                  <FaPhone className="text-primary mt-1 me-3 footer-contact-icon" />
                  <a href="tel:+201234567890" className="text-muted text-decoration-none">+20 123 456 7890</a>
                </li>
                <li className="d-flex mb-3 footer-contact-item">
                  <FaEnvelope className="text-primary mt-1 me-3 footer-contact-icon" />
                  <a href="mailto:info@elsamy-edu.com" className="text-muted text-decoration-none">info@elsamy-edu.com</a>
                </li>
              </ul>
            </div>
          </Col>
        </Row>
        
        <hr className="my-4 bg-secondary" />
        
        <Row>
          <Col md={6} className="text-center text-md-start animate-on-scroll" data-animation="animate__fadeInUp">
            <p className="mb-0 text-muted">
              <FaShieldAlt className="me-2 text-primary" />
              {currentYear} منصة السامي التعليمية. جميع الحقوق محفوظة
            </p>
          </Col>
          <Col md={6} className="text-center text-md-end animate-on-scroll" data-animation="animate__fadeInUp">
            <p className="mb-0 text-muted">
              تم التطوير بواسطة عبدالله مجدي
            </p>
          </Col>
        </Row>
      </Container>
    </footer>
  );
};

export default Footer;
